import { Store } from '@ngrx/store';
import { ErrorTracking } from '@shared/tracking';
import { Observable } from 'rxjs/Observable';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  DeleteRequestPayload,
  PushRequestPayload,
  RootModelAction,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { ApiHttp, ApiPostResponse } from './api-http.service';
import {
  Backend,
  backend,
  getDeletePayload,
  getPushPayload,
  getSetPayload,
  getUpdatePayload,
} from './backend';
import { assertNever, isDefined } from './helpers';
import { RootModel } from './root.model';
import { Path, RawQuery, Reference, StoreState } from './store.model';

export function push<K extends keyof RootModel>(
  ref: Reference<K>,
  object: RootModel[K],
  store: Store<StoreState>,
  apiHttp: ApiHttp,
  errorTracking: ErrorTracking,
  extra?: { [index: string]: string | number },
): Observable<boolean> {
  const { path, query } = ref;

  const payload = getPushPayload(ref, object, errorTracking, extra);
  if (!payload) {
    return Observable.of(false);
  }

  return Observable.of((<Backend<K>>backend[path.collection]).post).pipe(
    filter(isDefined),
    map(postConfigGenerator => postConfigGenerator(path.authUid, object)),
    tap(data => {
      const action: RootModelAction<K> = { type: `API_PUSH`, payload };
      store.dispatch(action);
    }),
    switchMap(config =>
      apiHttp.post(config, payload.rawRequest, payload.asJSON),
    ),
    catchError(data => {
      sendActions({ type: 'PUSH', payload }, path, query, data, store);
      return Observable.throw(data);
    }),
    map(data =>
      sendActions({ type: 'PUSH', payload }, path, query, data, store),
    ),
  );
}

export function set<K extends keyof RootModel>(
  ref: Reference<K>,
  id: number | string,
  object: RootModel[K],
  store: Store<StoreState>,
  apiHttp: ApiHttp,
  errorTracking: ErrorTracking,
): Observable<boolean> {
  const { path, query } = ref;

  return Observable.of((<Backend<K>>backend[path.collection]).post).pipe(
    filter(isDefined),
    map(post => post(path.authUid, object)),
    withLatestFrom(store),
    map(([config, storeState]) => ({
      config,
      payload: getSetPayload(ref, id, object, storeState, errorTracking),
    })),
    filter(isPayloadDefined),
    tap(({ payload }) => {
      const action: RootModelAction<K> = { type: `API_SET`, payload };
      store.dispatch(action);
    }),
    switchMap(
      ({ config, payload }) => apiHttp.post(config, payload.rawRequest),
      ({ payload }, data) => ({ payload, data }),
    ),
    map(({ payload, data }) =>
      sendActions<K>({ type: 'SET', payload }, path, query, data, store),
    ),
  );
}

export function update<K extends keyof RootModel>(
  ref: Reference<K>,
  id: number | string,
  object: Partial<RootModel[K]>,
  store: Store<StoreState>,
  apiHttp: ApiHttp,
  errorTracking: ErrorTracking,
): Observable<boolean> {
  return store.pipe(
    take(1),
    map(storeState =>
      getUpdatePayload(ref, id, object, storeState, errorTracking),
    ),
    filter(isDefined),
    tap(({ payload }) => {
      const action: RootModelAction<K> = { type: `API_UPDATE`, payload };
      store.dispatch(action);
    }),
    switchMap(
      ({ endpoint, payload, method }) => {
        switch (method) {
          case 'POST':
            return apiHttp.post({ endpoint, params: {} }, payload.rawRequest);
          case 'PUT':
            return apiHttp.put({ endpoint, params: {} }, payload.rawRequest);
          default:
            return assertNever(method);
        }
      },
      ({ payload }, data) => ({ payload, data }),
    ),
    map(({ payload, data }) =>
      sendActions<K>(
        { type: 'UPDATE', payload },
        payload.ref.path,
        payload.ref.query,
        data,
        store,
      ),
    ),
  );
}

export function remove<K extends keyof RootModel>(
  ref: Reference<K>,
  id: number | string,
  store: Store<StoreState>,
  apiHttp: ApiHttp,
  errorTracking: ErrorTracking,
): Observable<boolean> {
  return store.pipe(
    take(1),
    map(storeState => getDeletePayload(ref, id, storeState, errorTracking)),
    filter(isDefined),
    tap(({ payload }) => {
      const action: RootModelAction<K> = { type: `API_DELETE`, payload };
      store.dispatch(action);
    }),
    switchMap(
      ({ endpoint, payload, method }) => {
        switch (method) {
          case 'POST':
            return apiHttp.post({ endpoint, params: {} }, payload.rawRequest);
          case 'PUT':
            return apiHttp.put({ endpoint, params: {} }, payload.rawRequest);
          case 'DELETE':
            return apiHttp.delete({ endpoint, params: {} });
          default:
            return assertNever(method);
        }
      },
      ({ payload }, data) => ({ payload, data }),
    ),
    map(({ payload, data }) =>
      sendActions<K>(
        { type: 'DELETE', payload },
        payload.ref.path,
        payload.ref.query,
        data,
        store,
      ),
    ),
  );
}

function sendActions<K extends keyof RootModel>(
  baseAction:
    | { type: 'PUSH'; payload: PushRequestPayload<K> }
    | { type: 'SET'; payload: SetRequestPayload<K> }
    | { type: 'UPDATE'; payload: UpdateRequestPayload<K> }
    | { type: 'DELETE'; payload: DeleteRequestPayload<K> },
  path: Path<K>,
  query: RawQuery<RootModel[K]> | undefined,
  data: ApiPostResponse<K>,
  store: Store<StoreState>,
) {
  switch (data.status) {
    case 'success': {
      const action: RootModelAction<K> =
        baseAction.type === 'PUSH'
          ? {
              type: 'API_PUSH_SUCCESS',
              payload: { ...baseAction.payload, result: data.result },
            }
          : baseAction.type === 'SET'
            ? {
                type: 'API_SET_SUCCESS',
                payload: { ...baseAction.payload, result: data.result },
              }
            : baseAction.type === 'UPDATE'
              ? {
                  type: 'API_UPDATE_SUCCESS',
                  payload: { ...baseAction.payload, result: data.result },
                }
              : {
                  type: 'API_DELETE_SUCCESS',
                  payload: { ...baseAction.payload, result: data.result },
                };
      store.dispatch(action);
      return true;
    }
    case 'error': {
      const action: RootModelAction<K> =
        baseAction.type === 'PUSH'
          ? {
              type: 'API_PUSH_ERROR',
              payload: {
                ...baseAction.payload,
                message: data.message,
              },
            }
          : baseAction.type === 'SET'
            ? {
                type: 'API_SET_ERROR',
                payload: { ...baseAction.payload, message: data.message },
              }
            : baseAction.type === 'UPDATE'
              ? {
                  type: 'API_UPDATE_ERROR',
                  payload: { ...baseAction.payload, message: data.message },
                }
              : {
                  type: 'API_DELETE_ERROR',
                  payload: { ...baseAction.payload, message: data.message },
                };
      store.dispatch(action);
      return false;
    }
    default: {
      const action: RootModelAction<K> =
        baseAction.type === 'PUSH'
          ? {
              type: 'API_PUSH_ERROR',
              payload: {
                ...baseAction.payload,
                message: 'Unexpected API response',
                error: data,
              },
            }
          : baseAction.type === 'SET'
            ? {
                type: 'API_SET_ERROR',
                payload: {
                  ...baseAction.payload,
                  message: 'Unexpected API response',
                },
              }
            : baseAction.type === 'UPDATE'
              ? {
                  type: 'API_UPDATE_ERROR',
                  payload: {
                    ...baseAction.payload,
                    message: 'Unexpected API response',
                  },
                }
              : {
                  type: 'API_DELETE_ERROR',
                  payload: {
                    ...baseAction.payload,
                    message: 'Unexpected API response',
                  },
                };
      store.dispatch(action);
      return false;
    }
  }
}

function isPayloadDefined<C, P>(x: {
  config: C;
  payload?: P;
}): x is { config: C; payload: P } {
  return x.payload !== undefined;
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/http-actions.ts